import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'Pharmacy Freedom Fund';
const DEFAULT_DESCRIPTION = 'Investing in and supporting independent pharmacies to strengthen community healthcare.';
const DEFAULT_KEYWORDS = 'pharmacy investing, independent pharmacy funding, healthcare investment, pharmacy acquisition, Pharmacy Freedom Fund';
const DEFAULT_SITE_URL = 'https://pharmacyfreedomfund.com';
// Prefer a PNG/JPG for social previews (WhatsApp/Facebook)
const DEFAULT_IMAGE = `${DEFAULT_SITE_URL}/social-image.png`;

function joinKeywords(kw) {
    if (!kw) return DEFAULT_KEYWORDS;
    if (Array.isArray(kw)) return kw.filter(Boolean).join(', ');
    return kw;
}

const SEO = ({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    keywords,
    canonical,
    image = DEFAULT_IMAGE,
    type = 'website',
    noindex = false,
    schema,
}) => {
    const fullTitle = title ? `${title} | Pharmacy Freedom Fund` : DEFAULT_TITLE;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const metaKeywords = joinKeywords(keywords);
    const canonicalUrl = canonical || DEFAULT_SITE_URL;

    // Infer image type for og:image:type
    const lowerImg = (image || '').toLowerCase();
    const imgType = lowerImg.endsWith('.jpg') || lowerImg.endsWith('.jpeg')
        ? 'image/jpeg'
        : lowerImg.endsWith('.png')
            ? 'image/png'
            : lowerImg.endsWith('.gif')
                ? 'image/gif'
                : lowerImg.endsWith('.webp')
                    ? 'image/webp'
                    : undefined;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={metaKeywords} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
            {image && <meta property="og:image" content={image} />}
            {image && <meta property="og:image:secure_url" content={image.replace('http://', 'https://')} />}
            {imgType && <meta property="og:image:type" content={imgType} />}
            {/* Provide common dimensions for large summary cards (recommended but not required) */}
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Pharmacy Freedom Fund" />
            <meta property="og:site_name" content="Pharmacy Freedom Fund" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {image && <meta name="twitter:image" content={image} />}

            {/* Optional JSON-LD schema */}
            {schema && (
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
